// Hosanna room generations (MDNotes ADR 154). After a server-side reset the canonical
// document has a new identity: the IndexedDB copy of the old generation must never be
// merged back, so every generation gets its own database and old ones are deleted.
const GENERATION_KEY = 'hosanna-adorans-generation';
const STALE_KEY = 'hosanna-adorans-stale-databases';
const RELOAD_GUARD_PREFIX = 'hosanna-adorans-generation-reload:';
const BASE_DATABASE = 'hosanna-adorans-chwala-canonical-v1';

const safe = read => {try {return read();} catch {return null;}};
const local = () => safe(() => window.localStorage);
const session = () => safe(() => window.sessionStorage);

export function readHosannaGeneration(storage = local()) {
  const raw = safe(() => storage?.getItem(GENERATION_KEY));
  return raw && /^\d{1,9}$/.test(raw) ? Number(raw) : 0;
}

export function hosannaDatabaseName(generation) {
  return generation === 0 ? BASE_DATABASE : `${BASE_DATABASE}@g${generation}`;
}

function readStale(storage) {
  const parsed = safe(() => JSON.parse(storage.getItem(STALE_KEY) || '[]'));
  return Array.isArray(parsed) ? parsed.filter(name => typeof name === 'string') : [];
}

// Reloads at most once per target generation and tab; returns false when switching is impossible.
export function adoptHosannaGeneration(generation, {storage = local(), tab = session(), reload = () => window.location.reload()} = {}) {
  const guard = `${RELOAD_GUARD_PREFIX}${generation}`;
  if (!storage || safe(() => tab?.getItem(guard))) return false;
  const previous = readHosannaGeneration(storage);
  try {
    storage.setItem(STALE_KEY, JSON.stringify([...new Set([...readStale(storage), hosannaDatabaseName(previous)])]));
    storage.setItem(GENERATION_KEY, String(generation));
    tab?.setItem(guard, '1');
  } catch {return false;}
  if (readHosannaGeneration(storage) !== generation) return false;
  reload();
  return true;
}

export function purgeStaleHosannaDatabases(inUse, {storage = local(), deleteDatabase = name => window.indexedDB?.deleteDatabase(name)} = {}) {
  if (!storage) return [];
  const stale = readStale(storage);
  const deleted = stale.filter(name => !inUse.includes(name));
  deleted.forEach(name => safe(() => deleteDatabase(name)));
  const remaining = stale.filter(name => inUse.includes(name));
  safe(() => remaining.length ? storage.setItem(STALE_KEY, JSON.stringify(remaining)) : storage.removeItem(STALE_KEY));
  return deleted;
}
